/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Tech Tree page — public viewer of every craftable / buildable Palworld
 * player can unlock as they level up. Mirrors the bot's `/techlist`
 * command but interactive: every tech card is clickable and opens a
 * modal with the recipe + description + materials.
 *
 * Data: `GET /api/tech-tree` returns levels grouped 1-65, each entry
 * including its recipe (from structuredata or itemdata_crafting) and
 * material icons.
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Loader } from "@/components/Atoms";
import { X, Search, Sparkles, Hammer, Package, GitBranch, Home, Boxes } from "lucide-react";
import BaseStructuresTab from "@/pages/BaseStructuresTab";
import ItemsCatalogTab from "@/pages/ItemsCatalogTab";
import UsedInModal from "@/pages/codex/UsedInModal";

const ASSETS = process.env.REACT_APP_BACKEND_URL;

function resolveImg(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  if (path.startsWith("/")) return `${ASSETS}${path}`;
  // gamedata stores relative paths like "assets/tech/foo.webp"
  return `${ASSETS}/api/${path}`;
}

function TechCard({ tech, onClick }) {
  // Prefer local wiki image (older curated slugs); fall back to the
  // palworld.gg CDN icon that /api/tech-tree emits for 1.0 entries.
  const img = resolveImg(tech.wiki_image) || resolveImg(tech.icon);
  const ancient = !!tech.ancient;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`glass-card p-3 text-left transition-transform hover:-translate-y-0.5 cursor-pointer flex items-center gap-3 ${
        ancient
          ? "ring-1 ring-amber-300/30 hover:ring-amber-300/60 bg-amber-300/[0.03]"
          : "hover:ring-1 hover:ring-[var(--teal)]/40"
      }`}
      data-testid={`tech-card-${tech.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="w-12 h-12 rounded bg-black/40 flex items-center justify-center shrink-0 ring-1 ring-white/10">
        {img ? (
          <img
            src={img}
            alt={tech.name}
            className="w-10 h-10 object-contain"
            draggable={false}
            loading="lazy"
            onError={(e) => { if (tech.icon && e.currentTarget.src !== resolveImg(tech.icon)) e.currentTarget.src = resolveImg(tech.icon); else e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <Package size={20} className="text-[var(--muted)]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--bone)] truncate">{tech.name}</div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] flex items-center gap-2 mt-0.5">
          {tech.cost != null && (
            <span className={`flex items-center gap-1 ${ancient ? "text-amber-300" : ""}`}>
              <Sparkles size={10} /> {tech.cost} {ancient ? "AP" : "TP"}
            </span>
          )}
          {tech.recipe?.source && (
            <span className="text-[var(--teal)]">
              {tech.recipe.source === "structure"
                ? "Structure"
                : (tech.recipe.unlocks?.length > 0 ? "Set" : "Craftable")}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function TechModal({ tech, onClose, onMaterialClick }) {
  const heroImg = resolveImg(tech.wiki_image) || resolveImg(tech.icon);
  const recipe = tech.recipe || {};
  const ancient = !!tech.ancient;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4 backdrop-blur-sm fade-up"
      onClick={onClose}
      data-testid="tech-modal"
    >
      <div
        className="glass-card relative max-w-xl w-full max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--bone)] p-1 rounded-full hover:bg-white/5 z-10" aria-label="Close" data-testid="tech-modal-close">
          <X size={18} />
        </button>
        <div className="p-6 sm:p-7">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-20 h-20 rounded-lg bg-black/40 ring-1 ring-white/10 flex items-center justify-center shrink-0">
              {heroImg ? <img src={heroImg} alt={tech.name} className="w-16 h-16 object-contain" /> : <Package size={36} className="text-[var(--teal)]" />}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-semibold tracking-tight" data-testid="tech-modal-name">{tech.name}</h2>
              <div className="flex items-center gap-2 flex-wrap mt-1.5">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border border-white/10 px-2 py-0.5 rounded-full">
                  Level {tech.level}
                </span>
                {tech.cost != null && (
                  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full flex items-center gap-1 ${
                    ancient
                      ? "text-amber-300 border border-amber-300/30 bg-amber-300/5"
                      : "text-[var(--teal)] border border-[var(--teal-soft)]"
                  }`}>
                    <Sparkles size={10} /> {tech.cost} {ancient ? "Ancient Tech Point" : "Technology Point"}{tech.cost === 1 ? "" : "s"}
                  </span>
                )}
                {recipe.source && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--teal)] border border-[var(--teal-soft)] px-2 py-0.5 rounded-full">
                    {recipe.source === "structure"
                      ? "Buildable"
                      : (recipe.unlocks?.length > 0 ? "Structure Set" : "Craftable")}
                  </span>
                )}
              </div>
            </div>
          </div>

          {recipe.description ? (
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">Description</div>
              <p className="text-sm leading-relaxed text-white/85 whitespace-pre-wrap">{recipe.description}</p>
            </div>
          ) : (
            <p className="text-sm text-[var(--muted)] italic mb-5">No description on file for this entry.</p>
          )}

          {recipe.materials?.length > 0 && (
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2 flex items-center gap-2">
                <Hammer size={11} /> Crafting Materials
              </div>
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {recipe.materials.map((m, i) => {
                  const mimg = resolveImg(m.image);
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded bg-black/20 border border-white/5 hover:border-[var(--teal-soft)] hover:bg-[rgba(0,229,255,0.04)] cursor-pointer transition-colors"
                      onClick={() => onMaterialClick?.(m.item)}
                      title={`See every tech that needs ${m.item}`}
                      data-testid={`tech-modal-mat-${i}`}
                    >
                      {mimg ? (
                        <img src={mimg} alt={m.item} className="w-8 h-8 rounded object-contain bg-black/30 ring-1 ring-white/10 shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-black/30 ring-1 ring-white/10 shrink-0" />
                      )}
                      <span className="text-sm text-white/85 flex-1 truncate">{m.item}</span>
                      <span className="font-mono text-[var(--teal)] text-sm">×{m.qty}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[10px] text-[var(--muted)] mt-1.5 text-right">
                Tip: click any material to see every tech that uses it.
              </p>
            </div>
          )}

          {recipe.unlocks?.length > 0 && (
            <div className="mb-5" data-testid="tech-modal-unlocks">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2 flex items-center gap-2">
                <Package size={11} /> Unlocks ({recipe.unlocks.length})
              </div>
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {recipe.unlocks.map((u, i) => {
                  const uimg = resolveImg(u.image);
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded bg-black/20 border border-white/5 hover:border-[var(--teal-soft)] hover:bg-[rgba(0,229,255,0.04)] cursor-pointer transition-colors"
                      onClick={() => onMaterialClick?.(u.name)}
                      title={`See every tech that needs ${u.name}`}
                      data-testid={`tech-modal-unlock-${i}`}
                    >
                      {uimg ? (
                        <img src={uimg} alt={u.name} className="w-8 h-8 rounded object-contain bg-black/30 ring-1 ring-white/10 shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-black/30 ring-1 ring-white/10 shrink-0" />
                      )}
                      <span className="text-sm text-white/85 flex-1 truncate">{u.name}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[10px] text-[var(--muted)] mt-1.5 text-right">
                Each piece in this set has its own crafting cost — check its individual tech entry.
              </p>
            </div>
          )}

          {recipe.source && !recipe.materials?.length && !recipe.unlocks?.length && (
            <p className="text-sm text-[var(--muted)] italic mb-5">No crafting recipe on file for this entry.</p>
          )}

          {(recipe.build_work != null || recipe.weight != null || recipe.price != null || recipe.rarity != null) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              {recipe.build_work != null && <Stat label="Build Work" v={recipe.build_work} />}
              {recipe.weight != null     && <Stat label="Weight"     v={recipe.weight} />}
              {recipe.price != null      && <Stat label="Sells For"  v={recipe.price} />}
              {recipe.rarity != null     && <Stat label="Rarity"     v={recipe.rarity} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="rounded bg-black/30 border border-white/5 px-2.5 py-2 text-center">
      <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--muted)]">{label}</div>
      <div className="font-mono text-sm text-[var(--bone)]">{v}</div>
    </div>
  );
}

function TechTreeBrowser({ onMaterialClick, selected, setSelected, levels, loading }) {
  const [q, setQ] = useState("");
  // `selected` (tech-detail modal) and the material reverse-lookup
  // trigger are lifted to the outer TechTree component so the same
  // modals can be opened from sibling tabs (Base Structures, Items).
  //
  // `levels` is fetched once by the outer component and reused by
  // both this browser and the cross-tab `openTechByName` resolver
  // — saves a duplicate /tech-tree HTTP call on page load.

  // Find a tech entry by name across all loaded levels — used to
  // pivot from the "Used in" list back into the tech detail modal.
  // `findTechByName` removed — cross-tab pivot now uses
  // `openTechByName` on the outer TechTree component (it owns the
  // shared techIndex).

  // No useEffect for fetching levels — the outer TechTree component
  // owns the fetch and passes results down.

  const filtered = useMemo(() => {
    if (!q.trim()) return levels;
    const needle = q.trim().toLowerCase();
    return levels
      .map((lv) => ({
        ...lv,
        techs: lv.techs.filter((t) =>
          t.name.toLowerCase().includes(needle)
          || (t.recipe?.description || "").toLowerCase().includes(needle),
        ),
      }))
      .filter((lv) => lv.techs.length > 0);
  }, [levels, q]);

  return (
    <div className="space-y-3" data-testid="tech-tree-root">
      <div className="glass-card p-3 flex items-center gap-2 sticky top-2 z-10 backdrop-blur">
        <Search size={14} className="text-[var(--muted)] shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or description (e.g. 'workbench', 'sphere')…"
          className="input flex-1"
          data-testid="tech-search"
        />
        {q && (
          <button onClick={() => setQ("")} className="btn btn-ghost text-xs" data-testid="tech-search-clear">
            Clear
          </button>
        )}
      </div>

      {loading && <Loader />}

      {!loading && filtered.length === 0 && (
        <div className="glass-card p-8 text-center text-[var(--muted)] text-sm">No tech entries match &ldquo;{q}&rdquo;.</div>
      )}

      {!loading && filtered.map((lv) => (
        <section key={lv.level} className="glass-card p-4" data-testid={`tech-level-${lv.level}`}>
          <h2 className="text-sm font-semibold mb-3 flex items-baseline gap-2">
            <span className="text-[var(--teal)] font-mono text-base">Lv {lv.level}</span>
            <span className="text-[var(--muted)] text-xs uppercase tracking-wider">
              {lv.techs.length} unlock{lv.techs.length === 1 ? "" : "s"}
            </span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {lv.techs.map((t) => (
              <TechCard key={t.name} tech={t} onClick={() => setSelected(t)} />
            ))}
          </div>
        </section>
      ))}

      {selected && (
        <TechModal
          tech={selected}
          onClose={() => setSelected(null)}
          onMaterialClick={onMaterialClick}
        />
      )}
    </div>
  );
}

/** Outer page — owns the tab choice and the cross-tab modal state
 *  (selected tech + used-in material) so any tab can trigger the
 *  reverse-index lookup and pivot back into a Tech detail without
 *  prop-drilling.
 */
export default function TechTree() {
  // Tab choice persists across navigation so a user that toggled to
  // "Base Structures" doesn't get bounced back to "Tech Tree" when
  // they revisit the page.
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("tech-tree-tab") || "tech"; } catch { return "tech"; }
  });
  const setAndStore = (t) => {
    setTab(t);
    try { localStorage.setItem("tech-tree-tab", t); } catch { /* ignore quota / private-mode */ }
  };

  // Cross-tab modal state lifted here so any tab can open them.
  const [selectedTech, setSelectedTech] = useState(null);
  const [usedInMaterial, setUsedInMaterial] = useState(null);
  // Single source-of-truth fetch of the tech tree. Used by both
  // `<TechTreeBrowser>` (for rendering) and `openTechByName` below
  // (for cross-tab pivots from Items / Base Structures).
  const toast = useToast();
  const [techLevels, setTechLevels] = useState([]);
  const [techLoading, setTechLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/tech-tree")
      .then(({ data }) => { if (!cancelled) setTechLevels(data.levels || []); })
      .catch((e) => { if (!cancelled) { toast.error(apiError(e)); } })
      .finally(() => { if (!cancelled) setTechLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const techIndex = useMemo(() => {
    const flat = [];
    for (const lv of techLevels) for (const t of (lv.techs || [])) flat.push(t);
    return flat;
  }, [techLevels]);

  const openTechByName = (techName) => {
    const t = techIndex.find(
      (x) => (x.name || "").toLowerCase() === (techName || "").toLowerCase()
    );
    if (!t) {
      toast.error(`Couldn't find tech "${techName}" — please try again from the Tech Tree tab.`);
      return;
    }
    setUsedInMaterial(null);
    setSelectedTech(t);
    setAndStore("tech");
  };
  return (
    <div className="space-y-4 fade-up">
      <PageHeader
        title={
          tab === "structures" ? "Base Structures"
          : tab === "items"     ? "Items Codex"
          :                       "Tech Tree"
        }
        subtitle={
          tab === "structures"
            ? "Every buildable you can place in your base, sorted by category. Click any structure for its build cost and description."
            : tab === "items"
            ? "Every item, weapon, schematic, food, and consumable in Palworld — 1,969 entries with icons. Click any item for its details."
            : "Every craftable + buildable you can unlock as you level. Click any entry for the recipe and description."
        }
      />

      <div className="glass-card p-1 flex gap-1" data-testid="tech-tree-tabs">
        <TabButton
          active={tab === "tech"}
          onClick={() => setAndStore("tech")}
          icon={GitBranch}
          label="Tech Tree"
          testid="tab-tech-tree"
        />
        <TabButton
          active={tab === "structures"}
          onClick={() => setAndStore("structures")}
          icon={Home}
          label="Base Structures"
          testid="tab-base-structures"
        />
        <TabButton
          active={tab === "items"}
          onClick={() => setAndStore("items")}
          icon={Boxes}
          label="Items Codex"
          testid="tab-items-catalog"
        />
      </div>

      {tab === "tech" && (
        <TechTreeBrowser
          onMaterialClick={setUsedInMaterial}
          selected={selectedTech}
          setSelected={setSelectedTech}
          levels={techLevels}
          loading={techLoading}
        />
      )}
      {tab === "structures" && <BaseStructuresTab onMaterialClick={setUsedInMaterial} />}
      {tab === "items" && <ItemsCatalogTab onMaterialClick={setUsedInMaterial} />}

      {/* Reverse-index modal — lifted out of TechTreeBrowser so any
          tab can pop it open. Clicking a tech inside switches to the
          Tech Tree tab and opens that tech's detail. */}
      {usedInMaterial && (
        <UsedInModal
          material={usedInMaterial}
          onClose={() => setUsedInMaterial(null)}
          onTechClick={openTechByName}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 px-3 py-2 rounded text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
        active
          ? "bg-[rgba(0,229,255,0.08)] text-[var(--teal)] ring-1 ring-[var(--teal-soft)]"
          : "text-[var(--muted)] hover:text-white hover:bg-white/5"
      }`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}
